import { useState } from "react";
import { toast } from "sonner";
import { Modal, can } from "@/components/staff/Panel";
import RoleKpis from "@/components/perf/RoleKpis";
import { RolePerms, RoleStaff, RoleDetails } from "@/components/staff/RoleParts";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const TEXT = [["purpose", "Role purpose"], ["decision_authority", "Decision-making authority"], ["approvals", "Approval requirements"]];
const LISTS = [["responsibilities", "Core responsibilities"], ["duties", "Routine duties"], ["kpis", "Performance indicators (KPIs)"]];
const init = (r) => ({ label: r?.label || "", desc: r?.desc || "", department: r?.department || "", reports_to: r?.reports_to || "", ...Object.fromEntries(TEXT.map(([k]) => [k, r?.[k] || ""])),
  ...Object.fromEntries(LISTS.map(([k]) => [k, (r?.[k] || []).join("\n")])) });
const body = (v) => ({ ...v, ...Object.fromEntries(LISTS.map(([k]) => [k, v[k].split("\n")])) });

function ProfileForm({ v, setV, ro }) {
  const inp = (k, lb, area) => (<div key={k}><label className="lb">{lb}</label>{area
    ? <textarea className="sel" rows={3} disabled={ro} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`role-${k}`} />
    : <input className="sel" disabled={ro} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`role-${k}`} />}</div>);
  return (<>
    {inp("label", "Role name")}{inp("desc", "Short description (shown when assigning)")}{inp("department", "Department or functional area")}
    {TEXT.map(([k, lb]) => inp(k, lb, true))}
    {LISTS.map(([k, lb]) => inp(k, `${lb} · one per line`, true))}
    {inp("reports_to", "Reporting line (reports to)")}
    <p className="mu" style={{ marginTop: 8 }}>Responsibilities describe the job. They never give system access; only the ticked permissions do.</p>
  </>);
}

const TABS = [["details", "Details"], ["profile", "Responsibilities"], ["permissions", "Permissions"], ["staff", "Staff"], ["kpis", "KPIs"]];

export function RoleEdit({ r, data, onClose, onDone }) {
  const [tab, setTab] = useState(r ? "details" : "profile");
  const { user } = useAuth();
  const mg = data.can_manage && !r?.locked;
  const [v, setV] = useState(init(r));
  const fail = (e) => toast.error(errMsg(e));
  const create = () => api.post("/roles", body(v)).then(() => { toast.success("Draft role created with no permissions. Open it to tick permissions."); onDone(); onClose(); }).catch(fail);
  const saveProfile = () => api.put(`/roles/${r.id}/profile`, body(v)).then(() => { toast.success("Responsibilities saved · permissions unchanged"); onDone(); }).catch(fail);
  const ok = v.label.trim().length >= 3;
  return (
    <Modal onClose={onClose} w={720} id="role-modal"><h3>{r ? r.label : "New role (draft)"}</h3>
      {r && <div className="seg2" style={{ marginBottom: 10, width: "fit-content", flexWrap: "wrap" }}>{TABS.map(([t, l]) => <span key={t} className={tab === t ? "on" : ""} onClick={() => setTab(t)} data-testid={`role-tab-${t}`}>{l}</span>)}</div>}
      {r && tab === "details" && <RoleDetails r={r} />}
      {(!r || tab === "profile") && <ProfileForm v={v} setV={setV} ro={!!r && !mg} />}
      {!r && <p className="mu" style={{ marginTop: 6 }} data-testid="role-new-hint">The role starts with no permissions and is saved as a Draft. It gives no access and can't be assigned until you tick permissions and activate it.</p>}
      {r && tab === "permissions" && <RolePerms r={r} groups={data.groups} adminOnly={data.admin_only} mg={mg} onDone={onDone} />}
      {r && tab === "staff" && <RoleStaff r={r} staff={data.staff} mg={mg} onDone={onDone} />}
      {r && tab === "kpis" && <RoleKpis r={r} canEdit={can(user, "staff_perf") && can(user, "roles_manage")} />}
      <div className="g2" style={{ marginTop: 12 }}><button className="b g" onClick={onClose} data-testid="role-close">Close</button>
        {!r && <button className="b" disabled={!ok} onClick={create} data-testid="role-save">Create draft role</button>}
        {r && mg && tab === "profile" && <button className="b" disabled={!ok} onClick={saveProfile} data-testid="role-save-profile">Save responsibilities</button>}</div>
    </Modal>);
}
